import { useRef, useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Tabs, TabList, TabPanels, Tab, TabPanel, Box, Flex, Text, Button } from '@chakra-ui/react'

import { OfferHeader } from './OfferHeader'
import {
  BasicInfoTab,
  DiscountsTab,
  ExtraInsuranceTab,
  YearlyMilageTab,
} from './tabs/carkulka'
import { Check } from '../../icons'
import { formatDate } from '../../utils/formatDate'
import { offerStatusColors } from '../../data/offers/utils/offerStatusColors'
import { Offer, OfferVehicle } from 'brokertslib/src/dtos/Offer'
import { offerStatusEnum } from 'BrokerTsLib/src/enums/offerStatusEnum'
import { useMemo } from 'react'
import { useUpdateLastWizardStep } from '../../hooks/useWizard'
import { useSelectCalculation } from '../../hooks/useOffer'
import { insuranceParamsContext } from '../../layouts/Wizard'
import { AnimatedOfferWrapper, animationVariants } from './AnimatedOfferWrapper'
import { carkulkaOffersContext } from '../../context/CarkulkaOffersProvider'
import { Insurer, Risk } from 'BrokerTsLib/src/enums'

const OfferFooter = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box sx={{ bgColor: 'gray.100', borderBottomRadius: 'md' }} as="footer">
      {children}
    </Box>
  )
}

export const GetOfferDisplayId = (paramsId?: string, offerId?: number) => `${paramsId}/${offerId}`
export const GetOfferElementId = (offerId?: number) => `offer-${offerId}`

export const scrollToOfferWithOffset = (elementId: string) => {
  const element: HTMLElement | null = document.querySelector(`#${elementId}`)
  const insuranceInfo: HTMLElement | null = document.querySelector('.insurance-info')
  const offset = insuranceInfo?.getBoundingClientRect().height!
  const elementPosition = element?.getBoundingClientRect().top!
  const offsetPosition = elementPosition + window.pageYOffset - offset

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  })
}

export const OfferCar = ({ offer }: { offer: OfferVehicle }) => {
  const navigate = useNavigate()

  const { insuranceParams, insuranceParamsId: paramsId, refetchInsuranceParams } = useContext(insuranceParamsContext)
  const { showAll } = useContext(carkulkaOffersContext)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const offerRef = useRef<HTMLDivElement>(null)
  const { mutateAsync: selectOffer } = useSelectCalculation()
  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const displayId = GetOfferDisplayId(paramsId, offer.offerId)
  const elementId = GetOfferElementId(offer.offerId)
  const isContract = !!insuranceParams?.generalParams.contractNumber

  const hasError = (offer.errors?.length ?? 0) > 0 ||
      offer.parts.some(p => (p.riskId === Risk.POV || p.riskId === Risk.HAV) && (p.notAvailable ?? false))

  const status = useMemo(() => {
    let status = offerStatusEnum.Valid
    if (hasError) status = offerStatusEnum.Error
    else if (insuranceParams?.generalParams.selectedOfferId === offer.offerId) status = offerStatusEnum.Selected

    return status
  }, [hasError, insuranceParams?.generalParams.selectedOfferId])

  const color = offerStatusColors[status]

  const handleToggle = (included: boolean) => {
    setIsVisible(showAll || included)
  }

  const [tabIndex, setTabIndex] = useState(0)
  const handleTabsChange = (index: number) => {
    setTabIndex(index)
    scrollToOfferWithOffset(elementId)
    // scrollToOffer(elementId)
  }

  return (
    //<OfferWrapper id={elementId}>
    <AnimatedOfferWrapper
      id={elementId}
      ref={offerRef}
      animate={isVisible ? 'visible' : 'hidden'}
      transition={{ duration: 0.3 }}
      variants={animationVariants}
    >
      <OfferHeader
        displayId={displayId}
        company={{ id: offer.insurerId, name: offer.insurerNameFull }}
        status={status}
        color={color}
        offer={offer}
        isDisabled={isContract}
        onToggle={handleToggle}
        isVisible={isVisible}
      />

      <OfferFooter>
        <Tabs variant="default" colorScheme="primary"
          // onChange={() => scrollToOffer()}          
          onChange={handleTabsChange}
          index={tabIndex}
        >
          <TabPanels>
            <TabPanel>
              {tabIndex == 0 && <BasicInfoTab color={color} offer={offer} />}
            </TabPanel>
            <TabPanel>
              {tabIndex == 1 && <ExtraInsuranceTab offer={offer} onSavedHandler={() => setTabIndex(0)} />}
            </TabPanel>
            <TabPanel>
              {tabIndex == 2 && <DiscountsTab offer={offer} onSavedHandler={() => setTabIndex(0)} />}
            </TabPanel>
            {[Insurer.Pillow, Insurer.Allianz].includes(offer.insurerId, 0) &&
              <TabPanel>
                {tabIndex == 3 && <YearlyMilageTab offer={offer} onSavedHandler={() => setTabIndex(0)} />}
              </TabPanel>
            }
            {/*<TabPanel>
              <GapDefendTab
                defaultChecked={gapDefend}
                onChange={(value: boolean) => setGapDefend(value)}
              />
            </TabPanel> */}
          </TabPanels>

          <Flex
            sx={{
              display: {
                base: 'grid',
                md: 'flex',
              },
              rowGap: 2,
              px: [3, 3, 4],
              pb: [3, 3, 4],
              alignItems: 'center',
              justifyContent: { base: 'stretch', md: 'space-between' },
            }}
          >
            <TabList>
              <Tab>Základní přehled</Tab>
              <Tab>Připojištění</Tab>
              <Tab>Slevy</Tab>
              {[Insurer.Pillow, Insurer.Allianz].includes(offer.insurerId, 0) &&
                <Tab>Nájezd kilometrů</Tab>
              }

              {/* <Tab>
                {gapDefend && (
                  <CheckCircle width="16px" height="16px" sx={{ lineHeight: 0, mr: 2 }} />
                )}
                GAP defend
              </Tab> */}
            </TabList>

            <Flex
              sx={{
                gap: 8,
                alignItems: 'center',
                justifyContent: { base: 'space-between', lg: 'end' },
              }}
            >
              {false && (
                <Text variant="textMuted" sx={{ fontSize: 'sm' }} as="p">
                  Nabídka platí do {formatDate({ date: new Date() })}
                </Text>
              )}

              {status !== 'error' && !isContract && tabIndex == 0 &&
                <Button
                  colorScheme="primary"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  loadingText="Ukládám..."
                  onClick={() => {
                    setIsSubmitting(true)

                    selectOffer({ paramsId: paramsId!, offerId: offer.offerId })
                      .then(() => {
                        if ((insuranceParams?.generalParams.lastWizardStep ?? 3) < 4) {
                          updateStep({ paramsId: Number(paramsId), step: 4, stepsStep: 'carkulka' }).
                            then(() => {
                              refetchInsuranceParams().then(() => {
                                navigate(`/carkulka/wizard/4/${paramsId}`)
                              })
                            })
                        }
                        else {
                          refetchInsuranceParams().then(() => {
                            navigate(`/carkulka/wizard/4/${paramsId}`)
                          })
                        }
                      })
                      .finally(() => setIsSubmitting(false))
                  }}
                >
                  <Check />
                  Sjednat
                </Button>
              }
            </Flex>
          </Flex>
        </Tabs>
      </OfferFooter>
    </AnimatedOfferWrapper>
  )
}
